import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useBlocker } from "react-router-dom";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Avatar } from "@/components/avatar";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Field, Input, controlClass } from "@/components/ui/field";
import { FkSelect } from "@/components/ui/fk-select";
import { RefreshButton } from "@/components/ui/refresh-button";
import { LoadingNote } from "@/components/ui/loading";
import { Skeleton } from "@/components/ui/skeleton";
import { api, errorMessage } from "@/services/api";
import { queryKeys } from "@/services/queries";
import { useDelayed } from "@/hooks/use-delayed";
import { useTranslation } from "@/store/locale";
import { cn } from "@/utils/utils";

/**
 * The two session policies at three levels — organisation, role, person — the
 * way the API resolves them: the person's own value wins, then the strictest
 * of their roles, then the organisation. An empty box means "Default": inherit.
 * Each row is one line: idle time and password expiry side by side.
 *
 * The screen is one resource: `GET /access-policies` brings every level in one
 * call, and `PUT /access-policies` saves every edit in one call and answers
 * with the screen as it now stands.
 */

type PolicyField = "idle_timeout_minutes" | "password_change_interval_days";
const FIELDS: PolicyField[] = ["idle_timeout_minutes", "password_change_interval_days"];

interface Organization {
  id: number;
  name: string;
  idle_timeout_minutes: number | null;
  password_change_interval_days: number | null;
}

/** A subject joined to whatever policy it holds; policy_id is null when it inherits. */
interface PolicyValues {
  policy_id: number | null;
  idle_timeout_minutes: number | null;
  password_change_interval_days: number | null;
}

interface GroupRow extends PolicyValues {
  group_id: number;
  group_name: string;
}

interface UserRow extends PolicyValues {
  user_id: number;
  user_name: string;
  full_name: string;
  avatar: string | null;
}

interface Screen {
  organization: Organization | null;
  groups: GroupRow[];
  /** Only the people with a policy of their own. */
  users: UserRow[];
}

const SCREEN_KEY = ["/access-policies"] as const;

/** A person picked for a policy but not yet saved: a row with nothing set. */
const blankRow = (user_id: number, name: string): UserRow => ({
  user_id,
  user_name: name,
  full_name: name,
  avatar: null,
  policy_id: null,
  idle_timeout_minutes: null,
  password_change_interval_days: null,
});

export function PolicyLevels() {
  const t = useTranslation();
  const title = t("cfg.accessPolicy");
  const queryClient = useQueryClient();

  const screen = useQuery({
    queryKey: SCREEN_KEY,
    queryFn: async ({ signal }) => (await api.get<Screen>("/access-policies", { signal })).data,
  });
  const org = screen.data?.organization ?? null;
  // A refetch keeps the rows on screen; the skeletons above are for the first
  // read only. Delayed, so a fast answer shows nothing at all.
  const busy = screen.isFetching;
  const refreshing = useDelayed(busy && !screen.isPending);

  /**
   * Edits made but not yet sent, keyed `<subject>.<field>` where the subject
   * is `org`, `group:<id>` or `user:<id>`.
   *
   * Every box used to save itself on blur — one request per person, and a
   * half-saved page if one of them failed. They are collected here and written
   * together, so the screen is a form rather than a hundred small commitments.
   */
  const [drafts, setDrafts] = useState<Record<string, number | null>>({});
  const dirty = Object.keys(drafts).length > 0;

  /** The value on screen: what was typed, or failing that what the API holds. */
  const shown = (key: string, saved: number | null) => (key in drafts ? drafts[key]! : saved);

  const edit = (key: string, saved: number | null) => (value: number | null) =>
    setDrafts((was) => {
      const next = { ...was };
      // Typing a value back to what it already was is not a change.
      if (value === saved) delete next[key];
      else next[key] = value;
      return next;
    });

  const save = useMutation({
    mutationFn: async () => {
      // One request: the organisation's values and one entry per role or
      // person, a subject with both fields edited carrying both.
      const bySubject: Record<string, Record<string, number | null>> = {};
      for (const [key, value] of Object.entries(drafts)) {
        const [subject, field] = key.split(".") as [string, PolicyField];
        (bySubject[subject] ??= {})[field] = value;
      }
      const { org: organization, ...rest } = bySubject;
      const policies = Object.entries(rest).map(([subject, fields]) => {
        const [kind, id] = subject.split(":");
        return { [kind === "group" ? "group_id" : "user_id"]: Number(id), ...fields };
      });
      return (await api.put<Screen>("/access-policies", { organization, policies })).data;
    },
    onSuccess: (next) => {
      toast.success(t("common.saved"));
      setDrafts({});
      setAdded([]);
      // The answer is the screen as saved; the shell's resolved copy refetches.
      queryClient.setQueryData(SCREEN_KEY, next);
      void queryClient.invalidateQueries({ queryKey: queryKeys.effectivePolicy });
    },
    onError: (error) => toast.error(errorMessage(error, t("common.couldNotSave"))),
  });

  // Leaving with edits in hand would drop them silently. `withResolver` hands
  // back the decision so the app's own dialog asks, rather than the browser's.
  // Navigating away with unsaved rows asks first. react-router's blocker takes a
  // predicate and exposes `state`/`proceed`/`reset`; the `enableBeforeUnload` half
  // of the old call has no equivalent, so the tab-close warning is wired directly.
  const leaving = useBlocker(() => dirty);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const [added, setAdded] = useState<UserRow[]>([]);
  const [picking, setPicking] = useState(false);
  // Listed: anyone with a policy row, plus anyone just picked but not yet saved.
  const listed = screen.data?.users ?? [];
  const individuals = [
    ...listed,
    ...added.filter((a) => !listed.some((u) => u.user_id === a.user_id)),
  ];

  return (
    <div className="pr-5">
      <h2 className="text-title-2 text-heading">{title}</h2>
      <p className="text-fg-muted text-callout mt-1">{t("policy.text")}</p>
      <div className="mt-1 flex items-center justify-end gap-4">
        {/* A refresh keeps the levels — they are still true until the answer
            says otherwise — so they dim rather than turn back into skeletons,
            and this says what is happening to them. */}
        {refreshing ? <LoadingNote /> : null}
        <RefreshButton queryKeys={[SCREEN_KEY]} />
      </div>

      <div
        aria-busy={busy || undefined}
        className={cn(
          "transition-opacity duration-[var(--duration-base)]",
          busy && !screen.isPending && "opacity-55",
        )}
      >
        <h3 className="text-heading text-callout mt-6 font-medium">
          {t("policy.global").replace("{title}", title)}
        </h3>
        <div className="bg-surface-raised rounded-card mt-4 shadow-[var(--shadow-lift)]">
          {screen.isPending ? (
            <RowSkeleton />
          ) : org === null ? (
            <CreateOrganization
              onCreated={() => void queryClient.invalidateQueries({ queryKey: SCREEN_KEY })}
            />
          ) : (
            <LevelRow
              title={t("policy.system")}
              text={t("policy.globalText")}
              shown={(field) => shown(`org.${field}`, org[field])}
              onChange={(field) => edit(`org.${field}`, org[field])}
              required
            />
          )}
        </div>

        <h3 className="text-heading text-callout mt-8 font-medium">
          {t("policy.roleBased").replace("{title}", title)}
        </h3>
        <div className="mt-4 grid gap-3">
          {screen.isPending
            ? Array.from({ length: 2 }, (_, index) => (
                <div
                  key={index}
                  className="bg-surface-raised rounded-card shadow-[var(--shadow-lift)]"
                >
                  <RowSkeleton />
                </div>
              ))
            : null}
          {(screen.data?.groups ?? []).map((row) => (
            <div
              key={row.group_id}
              className="bg-surface-raised rounded-card shadow-[var(--shadow-lift)]"
            >
              <LevelRow
                title={row.group_name}
                text={t("policy.roleText").replace("{role}", row.group_name)}
                shown={(field) => shown(`group:${row.group_id}.${field}`, row[field])}
                onChange={(field) => edit(`group:${row.group_id}.${field}`, row[field])}
              />
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <h3 className="text-heading text-callout font-medium">
            {t("policy.individual").replace("{title}", title)}
          </h3>
          <button
            type="button"
            onClick={() => setPicking(true)}
            disabled={screen.isFetching}
            className="text-accent hover:bg-accent-soft text-callout inline-flex items-center gap-1 rounded-lg px-2 py-1 font-medium transition-colors disabled:pointer-events-none disabled:opacity-50"
          >
            <Plus className="size-4" aria-hidden /> {t("policy.addEmployee")}
          </button>
        </div>
        {picking ? (
          <div className="bg-surface-raised rounded-card mt-3 flex flex-wrap items-end gap-3 p-4 shadow-[var(--shadow-lift)]">
            <div className="min-w-64 flex-1">
              <Field label={t("policy.employee")} htmlFor="pick-person">
                <FkSelect
                  id="pick-person"
                  endpoint="/users/compact"
                  value=""
                  placeholder={t("policy.chooseEmployee")}
                  exclude={(option) => individuals.some((i) => i.user_id === option.id)}
                  onChange={(next, label) => {
                    const id = Number(next);
                    if (id) setAdded((a) => [...a, blankRow(id, label)]);
                    setPicking(false);
                  }}
                />
              </Field>
            </div>
            <Button variant="ghost" onClick={() => setPicking(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        ) : null}
        <ul className="divide-line mt-3 max-h-[26rem] divide-y overflow-y-auto">
          {individuals.map((row) => {
            const name = row.full_name || row.user_name;
            return (
              <li key={row.user_id} className="flex items-center justify-between gap-4 py-2.5">
                <span className="flex min-w-0 items-center gap-3">
                  <Avatar src={row.avatar} name={name} className="bg-accent-soft text-accent" />
                  <span className="text-fg text-callout truncate">{name}</span>
                </span>
                <Values
                  shown={(field) => shown(`user:${row.user_id}.${field}`, row[field])}
                  onChange={(field) => edit(`user:${row.user_id}.${field}`, row[field])}
                />
              </li>
            );
          })}
          {screen.isPending ? (
            <li>
              <RowSkeleton />
            </li>
          ) : null}
          {!screen.isPending && individuals.length === 0 ? (
            <li className="text-fg-muted text-callout py-6 text-center">
              {t("policy.nobody")}
            </li>
          ) : null}
        </ul>
      </div>

      {/* Pinned to the bottom of the screen rather than the end of the page: the
          three levels scroll, and a save button below them is a save button
          nobody finds while editing the first one. */}
      <ConfirmDialog
        open={leaving.state === "blocked"}
        onClose={() => leaving.reset?.()}
        onConfirm={() => {
          setDrafts({});
          leaving.proceed?.();
        }}
        tone="danger"
        title={t("policy.leaveTitle")}
        message={t("policy.leaveUnsaved")}
        confirmLabel={t("policy.discard")}
        cancelLabel={t("common.cancel")}
      />

      {dirty ? (
        <div className="bg-surface-overlay border-line sticky bottom-0 z-10 mt-6 flex flex-wrap items-center justify-end gap-3 rounded-t-xl border px-4 py-3 backdrop-blur-xl">
          <span className="text-fg-muted text-callout mr-auto tabular-nums">
            {t("policy.unsavedCount").replace("{n}", String(Object.keys(drafts).length))}
          </span>
          <Button
            variant="ghost"
            onClick={() => setDrafts({})}
            disabled={save.isPending || busy}
          >
            {t("policy.discard")}
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={busy}
            loading={save.isPending}
            loadingLabel={t("common.saving")}
          >
            {t("policy.saveChanges")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A `LevelRow`'s outline, for while the screen loads: the title, its line of
 * explanation, and the two value boxes, in the space they will take.
 */
function RowSkeleton() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-4">
      <div className="grid min-w-0 gap-2">
        <Skeleton className="w-40" />
        <Skeleton className="h-3 w-64" />
      </div>
      <span className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <Skeleton className="h-9 w-28 rounded-lg" />
        <Skeleton className="h-9 w-28 rounded-lg" />
      </span>
    </div>
  );
}

function LevelRow({
  title,
  text,
  shown,
  onChange,
  required,
}: {
  title: string;
  text: string;
  shown: (field: PolicyField) => number | null;
  onChange: (field: PolicyField) => (value: number | null) => void;
  required?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-4">
      <div className="min-w-0">
        <p className="text-heading text-callout font-semibold">{title}</p>
        <p className="text-fg-muted text-subhead mt-0.5 leading-[18px]">{text}</p>
      </div>
      <Values shown={shown} onChange={onChange} required={required} />
    </div>
  );
}

/** The two boxes of a row, on one line: idle minutes, then expiry days. */
function Values({
  shown,
  onChange,
  required,
}: {
  shown: (field: PolicyField) => number | null;
  onChange: (field: PolicyField) => (value: number | null) => void;
  required?: boolean | undefined;
}) {
  const t = useTranslation();
  const caption = {
    idle_timeout_minutes: t("cfg.idleTitle"),
    password_change_interval_days: t("cfg.passwordExpiry"),
  };
  const unit = {
    idle_timeout_minutes: t("cfg.minutes"),
    password_change_interval_days: t("cfg.days"),
  };
  return (
    <span className="flex flex-wrap items-center gap-x-6 gap-y-2">
      {FIELDS.map((field) => (
        <ValueInput
          key={field}
          label={caption[field]}
          unit={unit[field]}
          value={shown(field)}
          onChange={onChange(field)}
          required={required}
        />
      ))}
    </span>
  );
}

/** Commits on blur or Enter; an emptied box means inherit. */
function ValueInput({
  label,
  unit,
  value,
  onChange,
  required,
}: {
  label: string;
  unit: string;
  value: number | null;
  onChange: (value: number | null) => void;
  required?: boolean | undefined;
}) {
  const t = useTranslation();
  const [draft, setDraft] = useState(value?.toString() ?? "");
  const [seen, setSeen] = useState(value);
  // A save elsewhere (or a refetch) moves the truth; follow it without an effect.
  if (seen !== value) {
    setSeen(value);
    setDraft(value?.toString() ?? "");
  }
  const commit = () => {
    const next = draft.trim() === "" ? null : Number(draft);
    if (next === value) return;
    if (required && next === null) {
      setDraft(value?.toString() ?? "");
      return;
    }
    onChange(next);
  };
  return (
    <label className="text-callout flex shrink-0 items-center gap-2">
      <span className="text-fg-muted">{label}</span>
      <input
        type="number"
        min={1}
        inputMode="numeric"
        value={draft}
        placeholder={t("policy.default")}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
        className={cn(controlClass, "text-callout h-10 w-24 px-3 text-center")}
      />
      <span className="text-fg">{unit}</span>
    </label>
  );
}

/** No organisation row yet: the one place it gets created, with a name and both values. */
function CreateOrganization({ onCreated }: { onCreated: () => void }) {
  const t = useTranslation();
  const [name, setName] = useState("S&R Membership Shopping");
  const [idle, setIdle] = useState("");
  const [expiry, setExpiry] = useState("");
  const create = useMutation({
    mutationFn: () =>
      api.put("/organizations", {
        name,
        idle_timeout_minutes: idle ? Number(idle) : null,
        password_change_interval_days: expiry ? Number(expiry) : null,
      }),
    onSuccess: () => {
      toast.success(t("policy.orgCreated"));
      onCreated();
    },
    onError: (error) => toast.error(errorMessage(error, t("policy.orgCreateFailed"))),
  });
  return (
    <form
      className="grid gap-4 p-5 md:grid-cols-[minmax(0,1fr)_8rem_8rem_auto] md:items-end"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <Field label={t("policy.orgName")} htmlFor="org-name" hint={t("policy.orgHint")}>
        <Input id="org-name" required value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t("cfg.minutes")} htmlFor="org-idle">
        <Input
          id="org-idle"
          type="number"
          min={1}
          value={idle}
          onChange={(e) => setIdle(e.target.value)}
        />
      </Field>
      <Field label={t("cfg.days")} htmlFor="org-expiry">
        <Input
          id="org-expiry"
          type="number"
          min={1}
          value={expiry}
          onChange={(e) => setExpiry(e.target.value)}
        />
      </Field>
      <Button
        type="submit"
        size="lg"
        loading={create.isPending}
        loadingLabel={t("common.saving")}
      >
        {t("common.create")}
      </Button>
    </form>
  );
}
